import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  CubeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import HeroIllustration from '../components/HeroIllustration';

const Landing: React.FC = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const features = [
    {
      icon: CubeIcon,
      title: 'Inventory Management',
      description: 'Track every item in real-time, get low stock alerts, and manage multiple warehouses from one dashboard'
    },
    {
      icon: DocumentTextIcon,
      title: 'Purchase Orders',
      description: 'Create and send purchase orders in seconds, track status, and set up automatic reminders for vendors'
    },
    {
      icon: ShoppingBagIcon,
      title: 'Invoices & Billing',
      description: 'Generate professional GST-compliant invoices with automatic tax calculations and e-invoicing support'
    },
    {
      icon: ShoppingBagIcon,
      title: 'Retail Billing',
      description: 'Fast counter billing with barcode scanning, print or share invoices directly via WhatsApp'
    },
    {
      icon: UserGroupIcon,
      title: 'User Management',
      description: 'Add team members with role-based permissions and track all user activities from a central dashboard'
    },
    {
      icon: ChartBarIcon,
      title: 'Reports & Analytics',
      description: 'Get complete business insights with detailed reports on sales, purchases, profit, and inventory'
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      business: 'Kumar Hardware, Jaipur',
      quote: 'Everything is now on my phone. Managing inventory has never been this easy. Highly recommended!'
    },
    {
      name: 'Priya Sharma',
      business: 'Sharma Medicals, Indore',
      quote: 'GST reports are now automatic. Saves me hours every month during tax filing. Worth every penny!'
    },
    {
      name: 'Mohammed Anees',
      business: 'Anees Garments, Lucknow',
      quote: 'Managing 3 shops from one dashboard. Great savings on time and money. Excellent product!'
    }
  ];

  const pricingPlans = [
    {
      name: 'Small Business',
      price: '₹299',
      period: '/month',
      description: 'Perfect for single location retailers',
      features: [
        '5 Users',
        'Unlimited POs & Invoices',
        '1,000 Inventory Items',
        'Retail Billing Module',
        'Basic Reports',
        'Email + Phone Support'
      ],
      featured: false
    },
    {
      name: 'Growing Business',
      price: '₹599',
      period: '/month',
      description: 'For multi-location businesses',
      features: [
        '20 Users',
        'Unlimited Everything',
        '10,000 Inventory Items',
        'Multi-Warehouse Support',
        'Advanced Reports & Analytics',
        'Priority Support',
        'Barcode Scanning',
        'API Access'
      ],
      featured: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large distributors & manufacturers',
      features: [
        'Unlimited Users',
        'Unlimited Everything',
        'Dedicated Account Manager',
        'Custom Feature Development',
        '24/7 Premium Support',
        'On-premise Deployment Option',
        'Training & Onboarding Sessions'
      ],
      featured: false
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Businesses Trust Us' },
    { value: '₹100Cr+', label: 'Inventory Managed' },
    { value: '50+', label: 'Cities Across India' },
    { value: '4.8/5', label: 'Customer Rating' }
  ];

  return (
    <div className="bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold">
                <span className="text-indigo-600">khata</span>
                <span className="text-gray-900">.biz</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-indigo-600 transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-gray-700 hover:text-indigo-600 transition-colors">
                Pricing
              </a>
              <a href="#testimonials" className="text-gray-700 hover:text-indigo-600 transition-colors">
                Testimonials
              </a>
              <Link
                to="/login"
                className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-lg shadow-orange-500/30"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-4">
              <Link
                to="/login"
                className="text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors font-medium text-sm"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-50 via-white to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-6">
                <span className="mr-2">🎉</span>
                10,000+ businesses trust us
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4 leading-tight">
                <span className="text-indigo-600">khata.biz</span>
              </h1>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Your Complete<br />
                Business Management Platform
              </h2>

              <p className="text-xl text-gray-600 mb-4">
                Purchase Orders • Invoices • Inventory • Retail Bills
              </p>

              <p className="text-lg text-gray-600 mb-8">
                Everything you need to manage your business, all in one place
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/40 transform hover:-translate-y-0.5"
                >
                  Start Free Trial - 7 Days
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>

                <button className="inline-flex items-center justify-center px-8 py-4 bg-white text-indigo-600 text-lg font-semibold rounded-lg border-2 border-indigo-600 hover:bg-indigo-50 transition-all">
                  Watch Demo
                </button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  No credit card required
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  Setup in 5 minutes
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  Cancel anytime
                </div>
              </div>
            </div>

            {/* Right Column - Hero Illustration */}
            <div className="relative">
              <HeroIllustration />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-indigo-200 text-sm md:text-base">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Are These Your Daily Challenges?
            </h2>
            <p className="text-xl text-gray-600">
              We understand the pain points of running a business
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-xl border-2 border-red-100">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📋</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Inaccurate Inventory Tracking
              </h3>
              <p className="text-gray-600">
                Data scattered across registers, Excel files, and memory. Sometimes overstocked, sometimes out of stock.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl border-2 border-red-100">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Payment Tracking Nightmare
              </h3>
              <p className="text-gray-600">
                Hard to track who owes you money and who you need to pay. Confusing and time-consuming.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl border-2 border-red-100">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                No Business Insights
              </h3>
              <p className="text-gray-600">
                Don't know your profit, loss, or best-selling products. All guesswork, no data-driven decisions.
              </p>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-block bg-green-50 border-2 border-green-200 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-green-900 mb-3">
                ✅ All Problems Solved with khata.biz!
              </h3>
              <p className="text-green-700 text-lg">
                Real-time inventory • Auto payment reminders • Instant reports & analytics
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Your Business
            </h2>
            <p className="text-xl text-gray-600">
              Powerful features designed for modern businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl border-2 border-gray-100 hover:border-indigo-200 hover:shadow-xl transition-all group"
                >
                  <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 transition-colors">
                    <Icon className="h-7 w-7 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white text-lg font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              Try All Features Free for 7 Days
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Started in 4 Simple Steps
            </h2>
            <p className="text-xl text-gray-600">
              Setup your complete business management system in minutes
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Sign Up',
                description: 'Create your account with email or mobile number',
                icon: '📝'
              },
              {
                step: '2',
                title: 'Add Inventory',
                description: 'Import products manually or via Excel upload',
                icon: '📦'
              },
              {
                step: '3',
                title: 'Start Creating',
                description: 'Create purchase orders, invoices, and bills',
                icon: '✨'
              },
              {
                step: '4',
                title: 'Invite Team',
                description: 'Add employees and assign permissions',
                icon: '👥'
              }
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-6 rounded-xl shadow-lg text-center h-full">
                  <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <div className="text-4xl mb-3">{step.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {step.description}
                  </p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRightIcon className="h-8 w-8 text-indigo-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition-colors shadow-xl shadow-orange-500/30"
            >
              Start Your Free Trial
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by 10,000+ Business Owners
            </h2>
            <p className="text-xl text-gray-600">
              See what our customers have to say
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl border-2 border-gray-100 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-xl">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="ml-4">
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.business}</div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex text-yellow-400 mb-2">
                    {'★★★★★'}
                  </div>
                  <p className="text-gray-700 italic leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 mb-2">
              Starting at just ₹299/month
            </p>
            <p className="text-gray-600">
              That's ₹10 per day - less than a cup of chai!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 ${
                  plan.featured
                    ? 'border-4 border-indigo-600 shadow-2xl transform scale-105'
                    : 'border-2 border-gray-200 shadow-lg'
                }`}
              >
                {plan.featured && (
                  <div className="bg-indigo-600 text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    MOST POPULAR
                  </div>
                )}

                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {plan.name}
                </h3>

                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600 text-lg">{plan.period}</span>
                </div>

                <p className="text-gray-600 mb-6">{plan.description}</p>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  className={`block text-center px-6 py-4 rounded-lg font-semibold transition-all ${
                    plan.featured
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.price === 'Custom' ? 'Contact Sales' : 'Start Free Trial'}
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 text-gray-600">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                7-day free trial
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                Cancel anytime
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Got questions? We've got answers
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'Is the free trial really free?',
                a: 'Yes! No credit card required. Get full access to all features for 7 days, completely free.'
              },
              {
                q: 'Can I use it on my mobile phone?',
                a: 'Absolutely! Khata.biz works perfectly on mobile, tablet, and desktop. Manage your business on the go.'
              },
              {
                q: 'Is my data secure?',
                a: 'Yes! We use bank-level encryption and daily backups. Your data is completely secure and private. We never share it with anyone.'
              },
              {
                q: 'Can I import my existing data?',
                a: 'Yes! You can easily import your products and inventory from Excel files. Our team will help you migrate your data.'
              },
              {
                q: 'Do you provide training?',
                a: 'Yes! We provide video tutorials, documentation, and live support to help you get started. Our team will guide you step-by-step.'
              },
              {
                q: 'What happens after the trial ends?',
                a: 'You can choose to upgrade to a paid plan or your account will be paused. You can reactivate anytime by subscribing.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">
                    {faq.q}
                  </h3>
                  {openFaqIndex === index ? (
                    <ChevronUpIcon className="h-6 w-6 text-indigo-600 flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 pt-0">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Managing Your Business Better Today
          </h2>
          <p className="text-xl text-indigo-100 mb-4">
            Join 10,000+ businesses already using khata.biz
          </p>
          <p className="text-indigo-200 mb-8">
            Setup takes only 2 minutes • No credit card required • Cancel anytime
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-10 py-5 bg-orange-500 text-white text-xl font-bold rounded-lg hover:bg-orange-600 transition-all shadow-2xl hover:shadow-orange-500/50 transform hover:-translate-y-1"
            >
              Start Free Trial
              <ArrowRightIcon className="ml-3 h-6 w-6" />
            </Link>

            <button className="inline-flex items-center justify-center px-10 py-5 bg-white text-indigo-600 text-xl font-bold rounded-lg hover:bg-gray-100 transition-all shadow-xl">
              Schedule a Demo
            </button>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-6 text-indigo-100">
            <div className="flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 mr-2" />
              10,000+ businesses trust us
            </div>
            <div className="flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 mr-2" />
              4.8/5 average rating
            </div>
            <div className="flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 mr-2" />
              24/7 support available
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company */}
            <div>
              <div className="text-2xl font-bold mb-4">
                <span className="text-indigo-400">khata</span>
                <span className="text-white">.biz</span>
              </div>
              <p className="text-gray-400 mb-4">
                Complete business management platform for modern businesses
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">Facebook</span>
                  📘
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">Twitter</span>
                  🐦
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">LinkedIn</span>
                  💼
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">YouTube</span>
                  📺
                </a>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Tutorials</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Docs</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <PhoneIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-white">+91-XXXX-XXXXXX</div>
                    <div className="text-sm text-gray-400">Mon-Sat, 9 AM - 7 PM</div>
                  </div>
                </li>
                <li className="flex items-center">
                  <EnvelopeIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                  support@khata.biz
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2024 khata.biz. All rights reserved.
              </div>
              <div className="flex space-x-6 text-sm">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Terms of Service
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Cookie Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
